
import { useState, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import Box from '@mui/material/Box';

import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Link } from 'react-router-dom';

import { useAppState } from '../../reduxish/appState';
import ICasino, { getMinBet } from '../../models/ICasino'
import CasinoSortEnum from '../../models/CasinoSortEnum';
import CasinoListItem from './CasinoListItem';
import RefreshIndicator from '../RefreshIndicator';
import usePollingRefresh from '../../hooks/usePollingRefresh';
import { useRefreshSlot } from './RefreshSlotContext';
import { apiFetch } from '../../api/http';

const ITEM_SIZE = 140;

function possibleNullAscendingComparison(val1: number | null, val2: number | null): number {
    if (val1 === null) return Infinity;
    if (val2 === null) return -Infinity;
    return val1 - val2;
}

export default function CasinosList() {
    const [fullCasinos, setFullCasinos] = useState<ICasino[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');

    const { state } = useAppState();
    const { casinoSearch: { searchText, sortFieldType }, homeFavoritesOnly, favoriteOverrides } = state;
    const isAuthenticated = state.auth?.isAuthenticated ?? false;
    const favoritesOnly = isAuthenticated && homeFavoritesOnly;

    // Derive filtered+sorted list directly so we don't pay an extra render per keystroke
    const casinos = useMemo(() => {
        const needle = searchText.toLowerCase();
        const isFav = (c: ICasino) => favoriteOverrides[c.id] ?? c.isFavorite ?? false;
        let filtered = needle
            ? fullCasinos.filter(c => c.name.toLowerCase().includes(needle))
            : fullCasinos.slice();
        if (favoritesOnly) {
            filtered = filtered.filter(isFav);
        }
        filtered.sort((a, b) => {
            switch (sortFieldType) {
                case CasinoSortEnum.Rating: return b.rating - a.rating;
                case CasinoSortEnum.CrapsMinimumBet: return possibleNullAscendingComparison(getMinBet(a, 'craps'), getMinBet(b, 'craps'));
                case CasinoSortEnum.RouletteMinimumBet: return possibleNullAscendingComparison(getMinBet(a, 'roulette'), getMinBet(b, 'roulette'));
                case CasinoSortEnum.BlackjackMinimumBet: return possibleNullAscendingComparison(getMinBet(a, 'blackjack'), getMinBet(b, 'blackjack'));
                case CasinoSortEnum.Name: return a.name.localeCompare(b.name);
                default: return 0;
            }
        });
        return filtered;
    }, [searchText, sortFieldType, fullCasinos, favoritesOnly, favoriteOverrides]);

    // Load all casinos on mount
    useEffect(() => {
        setErrorMsg('');
        setIsLoading(true);
        apiFetch('/api/v1/casinos')
            .then(res => {
                if (!res.ok) throw new Error(`Failed to fetch casinos: ${res.status}`);
                return res.json() as Promise<ICasino[]>;
            })
            .then(data => {
                setFullCasinos(data);
                setIsLoading(false);
            })
            .catch(() => {
                setErrorMsg('There was an error retrieving the casino data. Check your internet connection and try refreshing the page.');
                setIsLoading(false);
            });
    }, []);

    // Background refresh: re-fetch and merge by id so existing list rows stay
    // mounted and only changed min-bet numbers animate via SlotNumber.
    const refreshFetcher = useCallback(async (signal: AbortSignal) => {
        const res = await apiFetch('/api/v1/casinos', { signal });
        if (!res.ok) throw new Error(`Failed to fetch casinos: ${res.status}`);
        const data = await res.json() as ICasino[];
        setFullCasinos(prev => {
            if (prev.length === 0) return data;
            const byId = new Map(data.map(c => [c.id, c]));
            const merged = prev.map(c => byId.get(c.id) ?? c);
            // Append any new casinos that weren't in the previous list.
            for (const c of data) {
                if (!prev.find(p => p.id === c.id)) merged.push(c);
            }
            return merged;
        });
    }, []);

    const { isRefreshing, lastUpdated, refreshNow } = usePollingRefresh(
        refreshFetcher,
        !isLoading && !errorMsg,
    );

    const { slot } = useRefreshSlot();

    if (isLoading) {
        return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: '20%' }} />;
    }

    if (errorMsg) {
        return <p>{errorMsg}</p>;
    }

    return (
        <>
            {slot && createPortal(
                <RefreshIndicator
                    isRefreshing={isRefreshing}
                    lastUpdated={lastUpdated}
                    onRefresh={refreshNow}
                />,
                slot,
            )}
            <List
                disablePadding
                sx={{
                    mt: 1,
                    // Let the browser skip rendering offscreen rows. Equivalent to
                    // virtualization but uses native page scrolling.
                    '& > *': {
                        contentVisibility: 'auto',
                        containIntrinsicSize: `${ITEM_SIZE}px`,
                    },
                }}
            >
                {casinos.map(casino => (
                    <Box key={casino.id}>
                        <CasinoListItem casino={casino} />
                    </Box>
                ))}
                {!isAuthenticated && (
                    <Box>
                        <ListItem sx={{ px: { xs: 0.5, sm: 2 }, pt: 3, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <LockOutlinedIcon color="action" fontSize="small" />
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                You're only seeing the 5 most popular casinos.{' '}
                                <Link to="/signin" style={{ color: 'inherit', fontWeight: 600 }}>Sign in</Link>
                                {' '}to find all of the best casinos!
                            </Typography>
                        </ListItem>
                    </Box>
                )}
            </List>
        </>
    );
}
